import { EChartsOption, SeriesOption } from 'echarts';
import { BasicChartDataSource, LegendLabelPriorityEntry } from '@app/data/interface/widget-config';
import { EnvironmentalDataSource } from '@app/data/services/types/EnvironmentalDataSource';
import moment from 'moment';
import { masterAnalytes } from './master-analytes';

/**
 * Options for the rendering of the data view table.
 */
export interface DataViewTableOptions {
  includeAxisColumn?: boolean;
}

/**
 * Maps the x-axis data for a chart.
 *
 * If the data points are objects with a 'name' property, uses that as the label.
 * If the data points are primitives, converts them to strings.
 * If a data point looks like a date, formats it via tryFormatDateString().
 *
 * @param x The x-axis configuration.
 * @returns The mapped x-axis data.
 */
const mapXAxisData = (x: XAxisLike) =>
  (x.data || []).map((dataPoint: ChartDataPoint) => {
    const raw =
      typeof dataPoint === 'object' && dataPoint !== null && 'name' in dataPoint
        ? String((dataPoint as any).name)
        : String(dataPoint);
    try {
      const formatted = tryFormatDateString(raw);
      return formatted ? formatted : raw;
    } catch (e) {
      return raw;
    }
  });

/**
 * Represents a single data point in a chart series.
 */
export interface ChartDataPoint {
  symbol?: string;
  symbolSize?: number;
  value: number;
  name?: string;
  mostCommon?: string;
}

/**
 * Represents a single row of data in the data view table.
 *
 * Due to the variety of data formats that can be encountered,
 * a row can be a ChartDataPoint object, a number, a string,
 * or undefined (for missing data).
 *
 * This probably should be refactored to be more specific per chart type.
 */
export type RowData = ChartDataPoint | number | string | undefined;

/**
 * Represents a data series for charting purposes.
 */
export interface ChartDataSeries {
  name: string;
  data: ChartDataPoint[];
  type?: string;
}

/**
 * Appends a pivoted series for a specific property.
 *
 * Used when constructing a data view table from object-based data points.
 *
 * @param propName The name of the property to pivot.
 * @param pivotedSeries The array of pivoted series to append to.
 * @param originalData The original data points.
 * @param seenNames A record of seen names for handling duplicates.
 * @param firstItemIsArray Indicates if the first item is an array.
 * @param dimensionLabels The labels for each dimension.
 */
const appendPivotedSeriesForProp = (
  propName: string,
  pivotedSeries: ChartDataSeries[],
  originalData: ChartDataPoint[],
  seenNames: Record<string, number>,
  firstItemIsArray: boolean,
  dimensionLabels?: Array<string | undefined>
) => {
  const rawProp = String(propName);
  const numericIndex = Number(rawProp);
  const hasNumericIndex = !Number.isNaN(numericIndex);
  const dimensionLabel =
    hasNumericIndex && Array.isArray(dimensionLabels) ? dimensionLabels[numericIndex]?.trim() : undefined;

  let displayName: string;
  if (dimensionLabel) {
    displayName = dimensionLabel;
  } else if (firstItemIsArray && hasNumericIndex) {
    displayName = numericIndex === 0 ? 'X Value' : numericIndex === 1 ? 'Y Value' : `Dimension ${numericIndex + 1}`;
  } else {
    displayName = rawProp;
  }

  if (seenNames[displayName] !== undefined) {
    const suffix = ++seenNames[displayName];
    displayName = `${displayName} (${suffix})`;
  } else {
    seenNames[displayName] = 0;
  }

  pivotedSeries.push({
    name: displayName,
    data: originalData.map(
      (dataPoint: ChartDataPoint) => ({ value: (dataPoint as any)?.[propName] } as ChartDataPoint)
    ),
  });
};

/**
 * Represents the x-axis configuration for a chart.
 */
type XAxisLike = { data?: ChartDataPoint[]; name?: string };

/**
 * A function that renders a cell in the data view table.
 * @param series The data series for the cell.
 * @param dataPoint The data point for the cell.
 * @returns The HTML string for the cell.
 */
export interface CellRenderer {
  (series: ChartDataSeries, dataPoint?: RowData): string;
}

/**
 * Normalises an axis label by removing line breaks and formatting.
 *
 * @param axisOption The axis configuration option.
 * @returns The normalised axis label or undefined if not applicable.
 */
const normaliseAxisLabel = (axisOption: any): string | undefined => {
  const axis = Array.isArray(axisOption) ? axisOption[0] : axisOption;
  const rawName = axis && typeof axis.name === 'string' ? axis.name.trim() : '';
  if (!rawName || rawName === 'PLACEHOLDER') return undefined;
  const parts = rawName
    .split(/\n+/)
    .map((part: string) => part.trim())
    .filter(Boolean);
  if (!parts.length) return undefined;
  if (parts.length === 1) return parts[0];
  const [first, ...rest] = parts;
  const suffix = rest.join(' ');
  return suffix ? `${first} (${suffix})` : first;
};

/**
 * Derives the dimension labels for a scatter plot from the ECharts option and series array.
 *
 * @param opt The ECharts option object.
 * @param seriesArr The array of chart data series.
 * @returns An array of dimension labels or undefined if not applicable.
 */
const deriveScatterDimensionLabels = (
  opt: EChartsOption,
  seriesArr: ChartDataSeries[]
): Array<string | undefined> | undefined => {
  const scatterSeries = Array.isArray(seriesArr)
    ? seriesArr.find((series) => series && series.type === 'scatter' && Array.isArray(series.data))
    : undefined;
  if (!scatterSeries) return undefined;

  const dimensionLabels: Array<string | undefined> = [];

  if (Array.isArray((scatterSeries as any).dimensions)) {
    (scatterSeries as any).dimensions.forEach((dim: any, idx: number) => {
      if (typeof dim === 'string' && dim.trim()) {
        dimensionLabels[idx] = dim.trim();
      }
    });
  }

  const xLabel = normaliseAxisLabel(opt.xAxis);
  const yLabel = normaliseAxisLabel(opt.yAxis);

  if (xLabel && !dimensionLabels[0]) dimensionLabels[0] = xLabel;
  if (yLabel && !dimensionLabels[1]) dimensionLabels[1] = yLabel;

  return dimensionLabels.some((label) => typeof label === 'string' && label.trim() !== '')
    ? dimensionLabels
    : undefined;
};

export function getTimestampedFileNameForWidget(prefix: string): string {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${prefix}-${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}_${pad(now.getHours())}-${pad(
    now.getMinutes()
  )}-${pad(now.getSeconds())}`;
}

/**
 * Formats a data view table from the ECharts option object.
 *
 * ECharts provides the optionToContent callback to generate custom HTML content
 * for the data view. This function extracts the xAxis and series data to build
 * a simple HTML table representation of the chart data.
 *
 * Provides some level of casting and validation to ensure the data is in the
 * expected format for various formatting helper functions.
 *
 * Displays images for any series named "icon" with image URLs in the data, otherwise
 * renders a dash if no image URL is provided.
 *
 * Handles pivoting of object-based data points into columns as a fallback, using
 * the 'name' property of each data point as the row label and each property
 * name as the column header.
 *
 * Handles scatter plot dimension labels where there are 2 axis dimensions of data values.
 *
 * If a rowMapOverride is provided, reformat seriesArr to create new series per mapping.
 *
 * Shows the series value next to the icon.
 *
 * Captures the updated seriesArr returned by the fallback helper so any removals
 * (e.g. candidate series used to derive axis labels) are respected.
 * getPivotedFallbackAxisData may modify `seriesArr` (it replaces the array
 * contents when pivoting). Capture its return to ensure we respect changes.
 *
 * Also performs a defensive deduplication of series names to ensure unique headers.
 *
 * @param opt ECharts option object
 * @param cellRenderer Optional custom cell renderer function
 * @param unitLabelRenderer Optional function to append corre
 * @param rowMapOverride Optional mapping of row labels to numeric values for pivoting
 * @returns HTML string for the data view table
 */
export const dataViewFormatter = (
  opt: EChartsOption,
  cellRenderer?: CellRenderer,
  unitLabelRenderer?: (titleCase: string) => string,
  rowMapOverride?: Record<string, number>
) => {
  const rawXAxis = opt.xAxis;
  let seriesArr = (opt.series || []) as unknown as ChartDataSeries[];
  const firstXAxis: XAxisLike | undefined = Array.isArray(rawXAxis)
    ? (rawXAxis[0] as XAxisLike)
    : (rawXAxis as XAxisLike);

  if (firstXAxis && Array.isArray(firstXAxis.data) && firstXAxis.name !== 'Radius Mode') {
    const axisData = mapXAxisData(firstXAxis);
    const headerLabel = firstXAxis.name || '';
    return axisData.length
      ? buildTable(headerLabel, axisData, seriesArr, cellRenderer, unitLabelRenderer)
      : '<div>No data available</div>';
  }

  let axisData: string[] = [];
  let headerLabel = '';
  const scatterDimensionLabels = deriveScatterDimensionLabels(opt, seriesArr);
  ({ axisData, headerLabel, seriesArr } = getFallbackAxisData(seriesArr, axisData, headerLabel));

  if (rowMapOverride && Object.keys(rowMapOverride).length > 0) {
    ({ axisData, seriesArr, headerLabel } = getPivotedRowOverride(rowMapOverride, axisData, seriesArr, headerLabel));
  } else {
    ({ axisData, headerLabel } = getPivotedFallbackAxisData(axisData, seriesArr, headerLabel, scatterDimensionLabels));
  }

  if (headerLabel) {
    seriesArr = seriesArr.filter((s) => s.name !== headerLabel);
  }

  const seen: Record<string, boolean> = {};
  seriesArr = seriesArr.filter((s) => {
    const n = String(s.name ?? '');
    if (seen[n]) return false;
    seen[n] = true;
    return true;
  });

  const includeAxisColumn = !(Array.isArray(scatterDimensionLabels) && scatterDimensionLabels.length > 0);

  return axisData.length
    ? buildTable(headerLabel, axisData, seriesArr, cellRenderer, unitLabelRenderer, { includeAxisColumn })
    : '<div>No data available</div>';
};

/**
 * Formats the data table, optionally using a custom cell renderer.
 *
 * @param header The header label for the table.
 * @param axisLabels The data for the table's rows.
 * @param seriesData The taonga data series for the table.
 * @param renderCustomCell Optional custom cell renderer function
 * @param unitLabelRenderer Optional function to append correct units
 * @returns The formatted HTML string for the table.
 */
export const buildTable = (
  header: string,
  axisLabels: string[],
  seriesData: ChartDataSeries[],
  renderCustomCell?: CellRenderer,
  unitLabelRenderer?: (titleCase: string) => string,
  options?: DataViewTableOptions
) => {
  const includeAxisColumn = options?.includeAxisColumn !== false;
  let table = '';
  table += '<table class="echarts-dataview-table">';
  table += formatHeader(header, seriesData, unitLabelRenderer, includeAxisColumn);
  table += '<tbody>';

  for (let i = 0, l = axisLabels.length; i < l; i++) {
    table += formatRow(
      i,
      axisLabels[i],
      seriesData,
      renderCustomCell ? renderCustomCell : renderDefaultCell,
      includeAxisColumn
    );
  }

  table += '</tbody></table>';

  return table;
};

/**
 * Formats a row of the data table.
 *
 * Skips rows for series named "Radius Mode" as these are used when constructing
 * pie charts and should not be displayed in tabular data.
 *
 * @param rowIndex The index of the row.
 * @param axisValue The value for the row's axis.
 * @param dataSeries The data series for the row.
 * @returns The formatted HTML string for the row.
 */
export const formatRow = (
  rowIndex: number,
  axisValue: string,
  dataSeries: ChartDataSeries[],
  cellRenderer: CellRenderer,
  includeAxisColumn: boolean = true
) => {
  let row = '<tr>';
  if (includeAxisColumn) {
    row += `<td>${axisValue}</td>`;
  }

  dataSeries.forEach((series: ChartDataSeries) => {
    if (series.name === 'Radius Mode') {
      return;
    }

    const dataPoint = series.data && series.data[rowIndex] !== undefined ? series.data[rowIndex] : 'null';
    row += `<td>${cellRenderer(series, dataPoint)}</td>`;
  });

  row += '</tr>';

  return row;
};

/**
 * Formats the title of a chart data series.
 *
 * Converts underscores to spaces, capitalizes each word, and trims whitespace.
 *
 * Matches and appends the unit for known analytes from the Taiao dashboard configuration.
 *
 * @param s The data series
 * @param fallbackHeaderLabel The fallback header label to use if the series name is not available
 * @param unitLabelRenderer Optional function to provide unit labels for known analytes
 * @returns The formatted title
 */
const formatSeriesTitle = (
  s: ChartDataSeries,
  fallbackHeaderLabel: string,
  unitLabelRenderer?: (titleCase: string) => string
) => {
  const rawLabel = s.type === 'pie' ? fallbackHeaderLabel : s.name;
  const labelStr = String(rawLabel ?? '');
  const spaced = labelStr
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const titleCase = spaced
    .split(' ')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');

  let unit = unitLabelRenderer ? unitLabelRenderer(titleCase) : undefined;

  if (unit) {
    return `${titleCase} (${unit})`;
  }

  return titleCase;
};

/**
 * Formats the header row of the data table.
 *
 * Skips headsers for series named "Radius Mode" as these are used when constructing
 * pie charts and should not be displayed in tabular data.
 *
 * @param header The header label for the table.
 * @param seriesData The data series to include in the header.
 * @param unitLabelRenderer Optional function to provide custom units appended to the header labels
 * @returns The formatted HTML string for the header row.
 */
export const formatHeader = (
  header: string,
  seriesData: ChartDataSeries[],
  unitLabelRenderer?: (titleCase: string) => string,
  includeAxisColumn: boolean = true
) => {
  let formattedHeader = '<thead><tr>';
  let fallbackHeaderLabel = 'count';
  if (includeAxisColumn) {
    formattedHeader += `<th>${header || ''}</th>`;
  }
  const titleMap: Record<string, { indices: number[]; originalNames: string[] }> = {};
  seriesData.forEach((s: ChartDataSeries, idx: number) => {
    if (s.name === 'Radius Mode') return;
    const titleCase = formatSeriesTitle(s, fallbackHeaderLabel, unitLabelRenderer);
    if (!titleMap[titleCase]) titleMap[titleCase] = { indices: [], originalNames: [] };
    titleMap[titleCase].indices.push(idx);
    titleMap[titleCase].originalNames.push(s.name);
    formattedHeader += `<th>${titleCase}</th>`;
  });

  formattedHeader += '</tr></thead>';
  return formattedHeader;
};

/**
 * Cell renderer for displaying decimal scores.
 *
 * @param series The series data.
 * @param dataPoint The data point to render.
 * @returns The rendered cell content.
 */
export const decimalScoreCellRenderer = (series: ChartDataSeries, dataPoint?: RowData): string => {
  return typeof dataPoint === 'number' ? String(dataPoint.toPrecision(2)) : String(dataPoint);
};

/**
 * Renders a cell in the data table.
 * This is the default renderer to handle text values.
 *
 * Also handles formatting for specific weather analytes to two decimal places.
 *
 * @param series Data data series to render
 * @param dataPoint The data point for the cell
 * @returns The HTML string for the cell
 */
const renderDefaultCell = (series: ChartDataSeries, dataPoint?: RowData) => {
  const isObject = typeof dataPoint === 'object' && dataPoint !== null && 'value' in (dataPoint as any);
  let rawCellValue: RowData;

  if (isObject) {
    rawCellValue = (dataPoint as ChartDataPoint).value;
  } else {
    rawCellValue = dataPoint as RowData;
  }

  const num = Number(rawCellValue);
  if (rawCellValue !== '' && rawCellValue !== null && rawCellValue !== undefined && isFinite(num)) {
    if (Number.isInteger(num)) return String(num);

    const fixed = num.toFixed(2);
    fixed.replace(/\.0+$/, '').replace(/(\.[0-9]*?)0+$/, '$1');
    rawCellValue = fixed;
  }

  if (rawCellValue === undefined || rawCellValue === null) {
    return 'N/A';
  }

  return String(rawCellValue);
};

/**
 * Map series data to match axisData order based on 'name' property of data points.
 *
 * @param series Series to remap
 * @param axisData The axis data labels
 * @returns The remapped series
 */
const seriesDataMapper = (series: ChartDataSeries, axisData: string[]): ChartDataSeries => {
  if (!Array.isArray(series.data) || !axisData || axisData.length === 0) return series;
  const firstItem = (series.data as any)[0];
  const itemsAreObjects = firstItem && typeof firstItem === 'object' && 'name' in firstItem;
  if (!itemsAreObjects) return series;

  const mapByName = new Map<string, ChartDataPoint>();
  (series.data as ChartDataPoint[]).forEach((dataPoint: any) => {
    if (dataPoint && typeof dataPoint === 'object' && dataPoint.name != null)
      mapByName.set(String(dataPoint.name), dataPoint as ChartDataPoint);
  });

  const remapped = axisData.map((label) => {
    const found = mapByName.get(String(label));
    return found ? ({ name: found.name, value: found.value } as ChartDataPoint) : (undefined as any);
  }) as ChartDataPoint[];

  return { ...series, data: remapped } as ChartDataSeries;
};

/**
 * Extracts axis data and header label from series if not found in xAxis.
 *
 * Picks a candidate series to derive axis labels from only if it contains object data points
 * with meaningful `name` values. If no such series exists, leaves axisData empty so the
 * pivot fallback logic can handle arrays of primitives (numbers) correctly.
 * Removes the candidate series from seriesArr so it is not also rendered as a data column.
 *
 * Handles pie chart series as well by looking for named data points. Prepares remaining
 * series (those that will become columns). For pie charts and similar structures where
 * series.data is an array of {name, value} objects, remaps each series' data
 * to match the axisData order so buildTable can index by row.
 *
 * Will ignore series named 'Radius Mode' as these are visual-only helper series for pies.
 *
 * Contains a final fallback to pivot the candidate into a single 'value' series
 * so the data view shows values next to the derived categories.
 *
 * @param seriesArr The array of chart data series.
 * @param axisData The current axis data array.
 * @param headerLabel The current header label.
 * @returns An object containing the updated axis data and header label.
 */
export const getFallbackAxisData = (seriesArr: ChartDataSeries[], axisData: string[], headerLabel: string) => {
  if (!Array.isArray(seriesArr) || seriesArr.length === 0) {
    return { axisData, headerLabel, seriesArr };
  }

  const isNamedDataPoint = (dataPoint: any) =>
    dataPoint && typeof dataPoint === 'object' && dataPoint.name != null && String(dataPoint.name).trim() !== '';

  const seriesHasNamedPoints = (series: ChartDataSeries) =>
    Array.isArray(series.data) && (series.data as any).some(isNamedDataPoint);

  const candidateWithNames = seriesArr.find(seriesHasNamedPoints);

  if (!candidateWithNames || !Array.isArray(candidateWithNames.data)) {
    return { axisData, headerLabel, seriesArr };
  }
  axisData = (candidateWithNames.data as ChartDataPoint[]).map((dataPoint: ChartDataPoint, idx: number) =>
    String(dataPoint?.name ?? (dataPoint as any)?.value ?? idx)
  );
  headerLabel = candidateWithNames.name || '';

  const remaining = seriesArr
    .filter((series) => series !== candidateWithNames)
    .map((series) => seriesDataMapper(series, axisData));
  const visibleRemaining = remaining.filter((s) => String(s?.name) !== 'Radius Mode');
  const remainingHasData = visibleRemaining.some(
    (series) =>
      Array.isArray((series as any).data) &&
      (series as any).data.some((data: any) => data !== undefined && data !== null)
  );

  if (visibleRemaining.length === 0 || !remainingHasData) {
    const valueSeries: ChartDataSeries = {
      name: 'value',
      data: (candidateWithNames.data as ChartDataPoint[]).map(
        (dataPoint: ChartDataPoint) => ({ name: dataPoint.name, value: dataPoint.value } as ChartDataPoint)
      ),
    };
    seriesArr = [valueSeries];
  } else {
    seriesArr = visibleRemaining;
  }

  return { axisData, headerLabel, seriesArr };
};

/**
 * Formats the label for a data point, attempting to parse date strings.
 * @param dataPoint The data point to format.
 * @param idx The index of the data point.
 * @param chosenLabelProp The property to use for the label, if any.
 * @returns The formatted label.
 */
const formatLabel = (dataPoint: ChartDataPoint, idx: number, chosenLabelProp: string | null): string => {
  const raw = chosenLabelProp ? (dataPoint as any)[chosenLabelProp] : dataPoint?.name ?? dataPoint?.value ?? idx;
  if (raw === undefined || raw === null) return String(idx);
  if (typeof raw === 'string') {
    const formatted = tryFormatDateString(raw);
    return formatted ?? String(raw);
  }
  return String(raw);
};

/**
 * Extracts axis data and header label from series if not found in xAxis.
 *
 * Handles pivoting of object-based data points into columns as a fallback, using
 * the 'name' property of each data point as the row label and each property
 * name as the column header.
 *
 * Tracks seen column names to ensure unique headers when pivoting.
 * Detects and renames duplicate column names by appending a numeric suffix.
 *
 * When the series data are primitives (numbers/nulls), creates stable
 * index-based labels (Sample 1, Sample 2, ...) which correctly represent the row order.
 * Prefers a nicer human label rather than numeric values which could be mistaken for meaningful categories.
 *
 * Handles scatter plot dimension labels where there are 2 axis dimensions of data values.
 *
 * Tries to pick a sensible property to use as the row label (x-axis values e.g. date_time).
 *
 * Excludes the chosen label property (if any) from pivoted columns so it doesn't become a data column.
 *
 * @param axisData The current axis data array.
 * @param seriesArr The current chart data series array.
 * @param headerLabel The current header label.
 * @returns An object containing the updated axis data and header label.
 */
const getPivotedFallbackAxisData = (
  axisData: string[],
  seriesArr: ChartDataSeries[],
  headerLabel: string,
  dimensionLabels?: Array<string | undefined>
) => {
  const needPivot =
    (!axisData || axisData.length === 0) &&
    Array.isArray(seriesArr) &&
    seriesArr[0] &&
    Array.isArray(seriesArr[0].data);

  if (!needPivot) {
    return { axisData, headerLabel };
  }

  const originalData = (seriesArr[0].data as ChartDataPoint[]) || [];
  const firstItem = originalData[0];
  const itemsAreObjects = originalData.length > 0 && typeof firstItem === 'object' && firstItem !== null;

  if (!itemsAreObjects) {
    axisData = originalData.map((_dataPoint: ChartDataPoint, idx: number) => `Sample ${idx + 1}`);
    headerLabel = seriesArr[0].name || headerLabel;
    return { axisData, headerLabel };
  }

  const firstItemIsArray = Array.isArray(firstItem);
  const propNames = Object.keys(firstItem || {});

  const labelCandidates = ['name', 'date_time', 'date', 'label', 'category', 'x'];
  let chosenLabelProp: string | null = null;
  for (const c of labelCandidates) {
    if (c in (firstItem as any)) {
      chosenLabelProp = c;
      break;
    }
  }

  axisData = originalData.map((dataPoint: ChartDataPoint, idx: number) => formatLabel(dataPoint, idx, chosenLabelProp));

  const originalSeriesName = seriesArr[0].name ?? '';
  const pivotedSeries: ChartDataSeries[] = [];
  const seenNames: Record<string, number> = {};
  const columnProps = propNames.filter((propName) => propName !== 'name' && propName !== chosenLabelProp);

  columnProps.forEach((prop) =>
    appendPivotedSeriesForProp(String(prop), pivotedSeries, originalData, seenNames, firstItemIsArray, dimensionLabels)
  );

  seriesArr.length = 0;
  seriesArr.push(...pivotedSeries);

  if (!headerLabel || columnProps.includes(headerLabel)) {
    headerLabel = originalSeriesName || 'Category';
  }
  return { axisData, headerLabel };
};

/**
 * Transforms a rowMapOverride object into a pivoted format suitable for charting.
 *
 * Represent each property of rowMapOverride as a row in the data view.
 * First column (axis) should be the property name, second column is the numeric value labeled 'value'.
 *
 * @param rowMapOverride The mapping of row labels to their numeric values.
 * @param axisData The current axis data array.
 * @param seriesArr The current chart data series array.
 * @param headerLabel The current header label.
 * @returns An object containing the updated axis data, series array, and header label.
 */
const getPivotedRowOverride = (
  rowMapOverride: Record<string, number>,
  axisData: string[],
  seriesArr: ChartDataSeries[],
  headerLabel: string
) => {
  const orderedKeys = Object.keys(rowMapOverride).sort((a, b) => (rowMapOverride[a] ?? 0) - (rowMapOverride[b] ?? 0));
  axisData = orderedKeys.map((k) => String(k));

  const valueSeries: ChartDataSeries = {
    name: 'value',
    data: orderedKeys.map((k) => ({ name: k, value: rowMapOverride[k] } as ChartDataPoint)),
  };

  seriesArr = [valueSeries];
  headerLabel = headerLabel || 'Category';

  return { axisData, seriesArr, headerLabel };
};

/**
 * Unwraps ECharts axisPointer raw values (arrays/objects) to the underlying primitive.
 */
const unwrapAxisPointerRaw = (params: any): any => {
  let raw = params && params.value !== undefined ? params.value : params;
  if (raw && typeof raw === 'object') {
    if (Array.isArray(raw)) {
      const stringCandidate = raw.find((value: any) => typeof value === 'string');
      return stringCandidate !== undefined ? stringCandidate : raw[0];
    }
    if ('value' in raw) return raw.value;
  }
  return raw;
};

/**
 * Tries to format a date string into a consistent format.
 *
 * Attempts strict parsing for known formats first to avoid mis-parsing,
 * then falls back to a loose parse if necessary.
 *
 * @param value The value to format.
 * @returns The formatted date string or null if formatting fails.
 */
const tryFormatDateString = (value: any): string | null => {
  if (typeof value !== 'string') return null;

  let date = moment(value, 'DD/MM/yyyy', true);
  if (date.isValid()) return date.format('DD/MM/YYYY');

  const isoLikeFormats = ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ssZ', 'YYYY-MM-DD'];
  date = moment(value, isoLikeFormats, true);
  if (date.isValid()) return date.format('DD/MM/YYYY');

  date = moment(value);
  if (date.isValid()) return date.format('DD/MM/YYYY');

  return null;
};

/**
 * Formats a numeric value as a percentage string.
 * @param value Numeric fraction (0..1) or numeric-like value
 * @param significantDigits The number of significant digits to include
 * @returns The formatted percentage string or null if formatting fails
 */
const formatNumberAsPercent = (value: any, significantDigits = 0): string | null => {
  const num = Number(value);
  if (!isFinite(num)) return null;
  const percentage = num * 100;
  if (!isFinite(percentage)) return null;
  if (percentage === 0) return '0%';
  const mag = Math.floor(Math.log10(Math.abs(percentage)));
  const decimals = Math.max(0, significantDigits - (mag + 1));
  return Number(percentage.toFixed(decimals)) + '%';
};

/**
 * Formats a numeric value as a decimal string.
 * @param value Numeric fraction (0..1) or numeric-like value
 * @param significantDigits The number of significant digits to include
 * @returns The formatted decimal string or null if formatting fails
 */
const formatNumberAsDecimal = (value: any, significantDigits = 0): string | null => {
  const num = Number(value);
  if (!isFinite(num)) return null;
  const decimal = num;
  if (!isFinite(decimal)) return null;
  if (decimal === 0) return '0';
  return Number(decimal.toFixed(significantDigits)).toString();
};

/**
 * Formats the axis pointer label with percentage fallback for ECharts.
 * @param params The ECharts axisPointer params object or value
 * @returns The formatted axis pointer label
 */
export const formatAxisPointerLabelPercent = (params: any): string => {
  const raw = unwrapAxisPointerRaw(params);
  if (raw === null || raw === undefined) return '';
  const date = tryFormatDateString(raw);
  if (date !== null) return date;
  const pct = formatNumberAsPercent(raw);
  if (pct !== null) return pct;
  return String(raw);
};

/**
 * Formats the axis pointer label with decimal fallback for ECharts.
 * @param params The ECharts axisPointer params object or value
 * @returns The formatted axis pointer label
 */
export const formatAxisPointerLabelDecimal = (params: any): string => {
  const raw = unwrapAxisPointerRaw(params);
  if (raw === null || raw === undefined) return '';
  const date = tryFormatDateString(raw);
  if (date !== null) return date;
  const pct = formatNumberAsDecimal(raw, 2);
  if (pct !== null) return pct;
  return String(raw);
};

/**
 * Converts a hex color string to an RGBA color string.
 * @param hex The hex color string (e.g. "#RRGGBB").
 * @param alpha The alpha value (0..1).
 * @returns The RGBA color string (e.g. "rgba(R, G, B, A)").
 */
export const hexToRgba = (hex: string, alpha: number): string => {
  if (!hex) return `rgba(0, 0, 0, ${alpha})`;
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Return a human-friendly display label for a data source.
 * Centralises the label heuristics so all charts share the same behaviour.
 */
export interface DisplayLabelOptions {
  legendLabelPriority?: LegendLabelPriorityEntry[];
  includeLocation?: boolean;
  includeAnalyte?: boolean;
  includeUnitsInName?: boolean;
}

// Helper to join label parts with proper formatting
// takes the first part as the main label, and appends others in parentheses
const formatPartsIntoLabel = (parts: (string | undefined)[]): string => {
  const filteredParts = parts.filter((part) => part && part.trim() !== '') as string[];
  if (filteredParts.length === 0) return '';
  const [first, ...rest] = filteredParts;
  if (rest.length === 0) return first;
  return `${first} (${rest.join(', ')})`;
};

export function getDisplayLabelForDataSource(
  chartDataSource: BasicChartDataSource | any,
  allEnvironmentalDataSources: EnvironmentalDataSource[] | null,
  idx: number,
  options?: DisplayLabelOptions
): string {
  if (!options) {
    options = { includeLocation: true, includeAnalyte: false, includeUnitsInName: false };
  }

  if (!options?.legendLabelPriority) {
    options.legendLabelPriority = [
      'configLabel',
      'humanReadableName',
      'analyte',
      'entity',
      'sourceId',
    ] as LegendLabelPriorityEntry[];
  }
  const matchedEnv = (allEnvironmentalDataSources || []).find(
    (source) => source.source_identifier === chartDataSource.environmentalDataSourceIdentifier
  );
  const envName = matchedEnv?.human_readable_name;
  const rawLabel = (chartDataSource as any).label;
  const analyte = (chartDataSource as any).analyteIdentifier;
  const entity = (chartDataSource as any).entityNames?.[0];
  const sourceId = (chartDataSource as any).environmentalDataSourceIdentifier;

  const analyteConfig = masterAnalytes[analyte];
  if (!analyteConfig) {
    console.error(`Analyte configuration not found for identifier: ${analyte}`);
  }

  const looksLikeUnit = typeof rawLabel === 'string' && rawLabel.length <= 5 && !rawLabel.includes(' ');

  const resolvers: Record<LegendLabelPriorityEntry, () => string | undefined> = {
    humanReadableName: () => envName || undefined,
    configLabel: () => {
      if (!rawLabel) return undefined;
      return rawLabel;
    },
    analyte: () => (analyte ? String(analyte) : undefined),
    entity: () => (entity ? String(entity) : undefined),
    sourceId: () => (sourceId ? String(sourceId) : undefined),
  };

  for (const key of options.legendLabelPriority || []) {
    const resolver = resolvers[key];
    if (!resolver) continue;
    let resolved = resolver();
    if (resolved) {
      // collect the parts to include
      let parts = [];

      // it is assumed that the resolver pulls the location/name first
      // the location can be ommitted if the location is the same as the name
      // for all parts
      if (options?.includeLocation) {
        parts.push(resolved);
      }

      if (analyteConfig) {
        if (options?.includeAnalyte) {
          parts.push(analyteConfig?.name);
        }
        if (options?.includeUnitsInName) {
          parts.push(analyteConfig?.unit);
        }
      }
      return formatPartsIntoLabel(parts);
    }
  }

  if (envName) return envName;
  if (rawLabel && !looksLikeUnit) return rawLabel;
  if (analyte) return analyte;
  if (entity) return entity;
  if (sourceId) return sourceId;
  if (rawLabel) return rawLabel;
  return `Series ${idx + 1}`;
}

/**
 * Formats a tooltip parameter line for ECharts.
 *
 * Normalizes various data point structures to extract the value,
 * then formats it as a percentage with three significant digits.
 *
 * @param parameter ECharts tooltip param object
 * @returns The formatted tooltip parameter line
 */
const formatTooltipParamLine = (parameter: any): string => {
  let raw: any = parameter.value;
  if (raw && typeof raw === 'object' && 'value' in raw) raw = raw.value;
  if (Array.isArray(raw)) raw = raw[1];
  let valueText = '-';
  const num = raw === '' || raw == null ? NaN : Number(raw);
  if (isFinite(num)) {
    valueText = formatNumberAsPercent(num, 3) ?? '-';
  }
  return `<div style="display:flex;justify-content:space-between;align-items:center;"><span style="white-space:nowrap">${parameter.marker} ${parameter.seriesName}</span><span style="margin-left:8px;text-align:right;white-space:nowrap">${valueText}</span></div>`;
};

/**
 * Formats the axis tooltip for ECharts.
 * @param params ECharts tooltip params object or array
 * @returns The formatted axis tooltip
 */
export const formatAxisTooltip = (params: any): string => {
  if (!params) return '';
  const list = Array.isArray(params) ? params : [params];
  const axisLabel = list[0]?.axisValueLabel ?? list[0]?.axisValue ?? '';
  const lines: string[] = [];
  if (axisLabel) {
    lines.push(`<div style="font-weight:bold;margin-bottom:6px;text-align:center;width:100%;">${axisLabel}</div>`);
  }
  lines.push(...(list.map(formatTooltipParamLine) as string[]));
  return lines.join('');
};

export const formatLabelWithWrapping = (value: string | undefined, maxLength: number = 15) => {
  if (!value) return '';

  if (value.length <= maxLength) {
    return value;
  }

  const words = value.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    if ((currentLine + ' ' + word).length <= maxLength) {
      currentLine += (currentLine ? ' ' : '') + word;
    } else {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.join('\n');
};

/**
 * Formats the chart tooltip for ECharts.
 * @param params ECharts tooltip params object or array
 * @returns The formatted chart tooltip
 */
export const formatChartTooltip = (params: any) => {
  if (!Array.isArray(params) || !params.length) {
    return '';
  }

  const seriesLines = params
    .filter((p: any) => {
      const value = typeof p.value === 'number' ? p.value : 0;
      return value > 0;
    })
    .map((p: any) => {
      const value = typeof p.value === 'number' ? p.value : 0;
      return `<tr><td  style="text-align: left;">${p.marker} ${p.seriesName}</td><td>${value.toFixed(2)}%</td></tr>`;
    })
    .join('');
  return `<table>${seriesLines}</table>`;
};

const extractTooltipValue = (item: any): number => {
  if (item == null) {
    return 0;
  }

  const directValue = item?.value;
  if (typeof directValue === 'number' && isFinite(directValue)) {
    return directValue;
  }

  if (directValue && typeof directValue === 'object') {
    if (typeof directValue.value === 'number' && isFinite(directValue.value)) {
      return directValue.value;
    }
    if (typeof directValue.avg === 'number' && isFinite(directValue.avg)) {
      return directValue.avg;
    }
  }

  const dataValue = item?.data?.value;
  if (typeof dataValue === 'number' && isFinite(dataValue)) {
    return dataValue;
  }

  if (dataValue && typeof dataValue === 'object') {
    if (typeof dataValue.value === 'number' && isFinite(dataValue.value)) {
      return dataValue.value;
    }
    if (typeof dataValue.avg === 'number' && isFinite(dataValue.avg)) {
      return dataValue.avg;
    }
  }

  return Number(dataValue ?? 0);
};

/**
 * Formats a stacked percentage tooltip so each series shows the
 * precise contribution for the hovered category only.
 */
export const formatStackedPercentTooltip = (params: any): string => {
  if (!Array.isArray(params) || !params.length) {
    return '';
  }

  const total = params.reduce((acc: number, item: any) => {
    const value = extractTooltipValue(item);
    return acc + (isFinite(value) ? value : 0);
  }, 0);

  if (total <= 0) {
    return '';
  }

  const rows = params
    .filter((item: any) => {
      const value = extractTooltipValue(item);
      return isFinite(value) && value > 0;
    })
    .map((item: any) => {
      const rawValue = extractTooltipValue(item);
      const percent = total > 0 ? (rawValue / total) * 100 : 0;
      const label = item?.data?.stackId ?? item.seriesName;
      return `<tr><td style="text-align:left;">${item.marker} ${label}</td><td>${percent.toFixed(1)}%</td></tr>`;
    })
    .join('');

  return `<table>${rows}</table>`;
};

/**
 * Map a raw value (or normalized fraction) to an ECharts data item or null using hide-zero rules.
 * Exported so chart components can reuse the logic.
 */
export const mapValueForChart = (value: any, shouldHideZeroes: boolean, hideZeroesThreshold: number | null): any => {
  if (value === null || value === undefined) return null;
  const shouldHideByThreshold =
    hideZeroesThreshold !== null && typeof value === 'number' && value <= hideZeroesThreshold;
  if ((value === 0 && shouldHideZeroes) || shouldHideByThreshold) {
    return {
      value: value === null ? 0 : value,
      itemStyle: { color: 'transparent' },
      label: { show: false },
    } as any;
  }
  return value;
};

/**
 * Build a complete ECharts options object from a template and the selected dataSources.
 * Prefers the human_readable_name from environmental data sources when available.
 */
export function buildEchartsConfigFromDataSources(
  templateOptions: EChartsOption | null,
  chartDataSources: BasicChartDataSource[],
  allEnvironmentalDataSources: EnvironmentalDataSource[] | null,
  defaultSeriesType: string = 'bar',
  options?: DisplayLabelOptions
): EChartsOption {
  const template = (templateOptions || {}) as any;
  const templateSeries: SeriesOption[] = (template.series as SeriesOption[]) || [];
  const colors: any[] = Array.isArray(template.color) ? (template.color as any[]).slice() : [];

  const rebuiltSeries: SeriesOption[] = (chartDataSources || []).map((dataSource: any, idx: number) => {
    const template = templateSeries[idx] || templateSeries[0] || ({} as SeriesOption);
    const dsLabel = getDisplayLabelForDataSource(dataSource, allEnvironmentalDataSources, idx, options);
    return {
      ...(template as any),
      name: dsLabel,
      data: [],
      type: (dataSource as any).type || (template as any).type || defaultSeriesType,
    } as SeriesOption;
  });

  const rebuilt: EChartsOption = {
    ...template,
    series: rebuiltSeries as any,
    legend: {
      ...(template.legend || {}),
      data: rebuiltSeries.map((series) => (series as any).name),
    },
  } as EChartsOption;

  if (colors && colors.length) (rebuilt as any).color = colors;
  return rebuilt;
}

export function formatLongLabel(value: any) {
  // Example: split label into lines if longer than 10 characters
  if (value.length > 10) {
    return value.split(' ').join('\n'); // Split by space and join with newline
  }
  return value;
}
